import { Box, ExternalLink, Tooltip } from '@color/continuum';
import type { ReactElement, ReactNode } from 'react';

import { IGV_PATH } from './paths';

/**
 * Build an in-browser IGV viewer path from a desktop IGV deep link.
 *
 * Desktop IGV deep links (e.g. `http://localhost:60151/load?...&locus=1:12345-12345`)
 * encode the target locus as a `locus` query param. The in-browser viewer reads
 * the same `locus` plus the run's reads via `ssr_id`, so we reuse the deep link's
 * locus to point the browser viewer at the same view.
 */
const buildBrowserIgvPath = (igvLink: string, ssrId: string): string => {
  const params = new URLSearchParams({ ssr_id: ssrId });
  let locus: string | null = null;
  try {
    locus = new URL(igvLink).searchParams.get('locus');
  } catch {
    locus = null;
  }
  if (locus) {
    params.set('locus', locus);
  }
  return `${IGV_PATH}?${params.toString()}`;
};

/**
 * Wraps a desktop IGV deep link with a tooltip offering the in-browser IGV
 * viewer for the same locus and run. `ssrId` is the sample sequence run whose
 * reads the viewer should load, which differs from the wrapped link's run for
 * CNV control links. Shown alongside the desktop link while the in-browser
 * viewer is being tested.
 */
export const IgvBrowserTooltip = ({
  igvLink,
  ssrId,
  header,
  children,
}: {
  igvLink: string;
  ssrId: string | number;
  header?: ReactNode;
  children: ReactElement;
}) => {
  return (
    <Tooltip
      title={
        <Box>
          {header != null && <Box>{header}</Box>}
          <ExternalLink
            to={buildBrowserIgvPath(igvLink, String(ssrId))}
            underline="always"
            sx={(theme) => ({
              color: theme.palette.common.white,
              textDecoration: 'underline',
              textDecorationColor: theme.palette.common.white,
            })}
          >
            Try IGV in the browser
          </ExternalLink>
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
};
